import { api, API_BASE, ApiError } from "./client";

// Approval chains — staff wire types and client. Mirrors
//   PhoenixCapital.Core/Common/ApprovalChainDefinition.cs (the definition)
//   PhoenixCapital.Core/Enums/Collection/ApprovalChainEnums.cs (enums, as strings)
//   PhoenixCapital.Core/DTOs/Collection/ApprovalChainDtos.cs (DTOs)
// and the staff routes of the approval-chains contract. JSON is camelCase with
// enums as strings.

export type ApprovalChainRole = "Broker" | "Seller" | "Buyer" | "BuyerSideSigner" | "Phoenix";
export type ApprovalChainAction = "Endorse" | "Sign" | "Countersign";
export type ApprovalChainStatus = "Draft" | "Active" | "Returned" | "Cancelled" | "Completed";
export type ApprovalChainStageStatus = "Waiting" | "Active" | "Completed" | "Returned" | "Void";
export type ApprovalChainContentMode = "Merge" | "Upload";

/** One stage of a chain definition (template default or one send's chain). */
export interface ApprovalChainStageDefinition {
  role: ApprovalChainRole;
  mandatory: boolean;
  caption: string;
  statement: string;
  /** Buyer stage only — captions the co-borrower / guarantor blocks. */
  coSignerCaption?: string | null;
  coSignerStatement?: string | null;
  /** Per-send only — the Buyer portal accounts staff picked as co-signers. */
  coSignerPortalUserIds?: string[] | null;
}

export interface ApprovalChainDefinition {
  stages: ApprovalChainStageDefinition[];
}

/** Body of PUT …/items/{itemId}/chain. PATCH semantics: an omitted (or null)
 *  member is unchanged — send ONLY what the user changed. */
export interface ItemApprovalChainRequest {
  definition?: ApprovalChainDefinition;
  useTemplateDefault?: boolean;
  engagementAttested?: boolean;
  clearanceAttested?: boolean;
  contentMode?: ApprovalChainContentMode;
}

export interface ApprovalChainStageDto {
  id: string;
  documentVersion: number;
  order: number;
  role: ApprovalChainRole;
  roleLabel: string;
  action: ApprovalChainAction;
  partyName: string;
  partyEmail: string | null;
  partyTitle: string | null;
  partyCompany: string | null;
  caption: string;
  statement: string;
  status: ApprovalChainStageStatus;
  statusLabel: string;
  activatedAt: string | null;
  completedAt: string | null;
  returnComment: string | null;
  returnedAt: string | null;
  signatureEvidenceId: string | null;
}

export interface ApprovalChainAttestationDto {
  userId: string;
  userName: string | null;
  at: string;
  wording: string;
}

export interface ApprovalChainDto {
  id: string;
  collectionItemId: string;
  status: ApprovalChainStatus;
  statusLabel: string;
  currentOrder: number;
  documentVersion: number;
  isBorrowerFacing: boolean;
  contentMode: ApprovalChainContentMode;
  uploadFileName: string | null;
  bodySha256: string | null;
  /** Draft: the per-send override (null = the template default). Sent: the
   *  frozen chain. */
  definition: ApprovalChainDefinition | null;
  /** The template lineage's current default — the composer's baseline. */
  templateDefault: ApprovalChainDefinition | null;
  stages: ApprovalChainStageDto[];
  /** Earlier document versions' stage rows (Void), newest first. */
  history: ApprovalChainStageDto[];
  engagementAttestation: ApprovalChainAttestationDto | null;
  clearanceAttestation: ApprovalChainAttestationDto | null;
  startedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  canResendNotification: boolean;
  canCancel: boolean;
  canRevise: boolean;
  canCountersign: boolean;
}

export interface ApprovalChainPreflightPartyDto {
  order: number;
  role: ApprovalChainRole;
  roleLabel: string;
  resolved: boolean;
  partyName: string | null;
  partyEmail: string | null;
  problem: string | null;
}

export interface ApprovalChainPreflightDto {
  parties: ApprovalChainPreflightPartyDto[];
  problems: string[];
  /** Server-computed (Problems.Count == 0). The client reads `problems`. */
  canSend?: boolean;
}

export interface ReviseApprovalChainRequest {
  /** Edited sender details; omit (null) to keep the returned version's. */
  savedFormDataJson?: string | null;
  note?: string | null;
}

export interface EndorsementRecordDto {
  id: string;
  ownerEntityType: string;
  ownerEntityId: string;
  collectionItemId: string;
  documentName: string;
  templateId: string;
  templateVersion: number;
  documentVersion: number;
  bodySha256: string;
  intermediaryName: string;
  intermediaryBrokerageName: string | null;
  intermediaryBrokerId: string | null;
  intermediaryPortalUserId: string;
  endorsedAt: string;
  receivingPortal: string;
  receivingPortalUserId: string | null;
  auditLogId: string | null;
}

/** Read-only hint (ruling Q2): a guarantor party row on the deal's loan. */
export interface LoanGuarantorHint {
  name: string;
  email: string;
  portalUserId?: string | null;
}

/** A Buyer portal account the co-signer picker can add to the Buyer stage. */
export interface BuyerAccountOption {
  portalUserId: string;
  fullName: string;
  email: string;
}

const chainPath = (packageId: string, itemId: string) =>
  `/collection/packages/${packageId}/items/${itemId}/chain`;

export const approvalChainsApi = {
  /** The item's chain, or null when the item has none (the route 404s). */
  getForItem: async (itemId: string): Promise<ApprovalChainDto | null> => {
    try {
      return await api.get<ApprovalChainDto>(`/collection/items/${itemId}/chain`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  save: (packageId: string, itemId: string, body: ItemApprovalChainRequest) =>
    api.put<ApprovalChainDto>(chainPath(packageId, itemId), body),
  upload: (packageId: string, itemId: string, file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return api.upload<ApprovalChainDto>(`${chainPath(packageId, itemId)}/upload`, form);
  },
  preflight: (packageId: string, itemId: string) =>
    api.get<ApprovalChainPreflightDto>(`${chainPath(packageId, itemId)}/preflight`),
  resendNotification: (itemId: string) =>
    api.post<ApprovalChainDto>(`/collection/items/${itemId}/chain/resend-notification`),
  cancel: (itemId: string, reason: string) =>
    api.post<ApprovalChainDto>(`/collection/items/${itemId}/chain/cancel`, { reason }),
  revise: (itemId: string, body: ReviseApprovalChainRequest) =>
    api.post<ApprovalChainDto>(`/collection/items/${itemId}/chain/revise`, body),
  countersign: (itemId: string) =>
    api.post<ApprovalChainDto>(`/collection/items/${itemId}/chain/countersign`),
  /** A 15-minute SAS url of the current composed PDF. */
  documentUrl: (itemId: string) =>
    api.get<{ url: string }>(`/collection/items/${itemId}/chain/document`),
  endorsementRecords: (ownerEntityType: string, ownerEntityId: string) =>
    api.get<EndorsementRecordDto[]>("/collection/endorsement-records", {
      ownerEntityType,
      ownerEntityId,
    }),
  loanGuarantors: (dealId: string) =>
    api.get<LoanGuarantorHint[]>("/collection/approval-chain/loan-guarantors", { dealId }),
  buyerAccounts: (q: string) =>
    api.get<BuyerAccountOption[]>("/collection/approval-chain/buyer-accounts", { q }),
};

/** The CSV export is a file download served with the staff cookie, so the
 *  page links to it directly. */
export function endorsementRecordsExportUrl(ownerEntityType: string, ownerEntityId: string): string {
  const qs = new URLSearchParams({ ownerEntityType, ownerEntityId });
  return `${API_BASE}/collection/endorsement-records/export?${qs.toString()}`;
}
